import { useEffect, useState, useRef } from 'react';
import { z } from 'zod';
import type { StringKeyValue } from '~/types';

export type IFormError = {
  [key: string]: string[];
};

export interface IFormReturnData {
  data: StringKeyValue;
  onChange: (name: string, value: string) => void;
  clear: () => void;
  isValid: () => boolean;
  hasChanges: boolean;
  errors: IFormError;
}

export type UseFormProps = {
  initialData: StringKeyValue;
  validation?: z.ZodSchema;
};

export const useFormData = ({ initialData, validation }: UseFormProps) => {
  const [init, setInit] = useState(initialData);
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState<IFormError>({});
  const hasUserChanges = useRef(false);

  const hasChanges = JSON.stringify(init) != JSON.stringify(data);

  useEffect(() => {
    const initialDataString = JSON.stringify(initialData);
    const currentInitString = JSON.stringify(init);

    function updateStates() {
      setInit(initialData);
      setData(initialData);
    }

    if (initialDataString !== currentInitString && !hasUserChanges.current) {
      updateStates();
    }
  }, [initialData, init]);

  const onChange = (name: string, value: string) => {
    hasUserChanges.current = true;
    setData((prev: StringKeyValue) => ({ ...prev, [name]: value }));
  };

  const clear = () => {
    hasUserChanges.current = false;
    setData(init);
    setErrors({} as IFormError);
  };

  const isValid = (): boolean => {
    if (validation === undefined) return true;

    const result = validation?.safeParse(data);

    if (result.success) return true;

    const errors = z.flattenError(result.error).fieldErrors;
    setErrors(errors);
    return false;
  };

  return {
    data,
    onChange,
    clear,
    isValid,
    hasChanges,
    errors,
  };
};
